import { useEffect, useRef, MutableRefObject } from 'react';

import { useAbbConnector } from './useAbbConnector';
import { useCreoConnector } from './useCreoConnector';
import { useTMConnector } from './useTMConnector';
import { useAssignment } from './useAssignment';
import { useToast, ToastApi } from './useToast';
import { QueryParams } from '../utils/QueryParams';
import { Connector } from '../connector/Interface';
import { MTLData } from '../connector/CreoConnector';
import { InboundPartnr, Material, SurfaceTreatment, MaterialColor, MaterialDimension, AlternateMaterial } from '../types/CommonTypes';
import { ConditionType } from './usePartNrAssignment';

export type LeftConnectorType = 'abb' | 'creo';

export interface LeftConnectorApi {
    connector: MutableRefObject<Connector | null>;
    connectorType: LeftConnectorType;
    assignMaterial: () => void;
    assignAlternateMaterial: () => void;
    assignSurfaceTreatment: () => void;
    sendAssignment: (partNrs: InboundPartnr[], material?: Material, surfaceTreatment?: SurfaceTreatment, condition?: MaterialColor | MaterialDimension | null, conditionType?: ConditionType | null, alternates?: AlternateMaterial[]) => void;
    sendMTL?: () => void;
    toast: ToastApi;
}

export function useConnectors(): LeftConnectorApi {
    const assignment = useAssignment();
    const abb = useAbbConnector();
    const creo = useCreoConnector();
    const { mtlFile } = useTMConnector();
    const toast = useToast();

    const connectorType: LeftConnectorType = QueryParams.useCreoConnector ? 'creo' : 'abb';
    const active = connectorType === 'creo' ? creo : abb;
    const pendingMTLData = useRef<MTLData | undefined>(undefined);

    useEffect(() => {
        if (!mtlFile) return;
        pendingMTLData.current = {
            name: mtlFile.parsed?.ND_RelParSet_K01?.Name,
            parameters: mtlFile.parsed?.ND_RelParSet_K01?.PARAMETERS,
            rawMTLString: new TextDecoder('utf-8').decode(mtlFile.buffer),
        };
        toast.show('MTL export received');
        if (connectorType === 'creo' && assignment.pendingMaterial?.id) {
            assignment.assignMaterial();
        }
    }, [mtlFile]);

    function assignMaterial(): void {
        assignment.assignMaterial();
    }

    function assignAlternateMaterial(): void {
        assignment.assignAlternateMaterial();
    }

    function assignSurfaceTreatment(): void {
        assignment.assignSurfaceTreatment();
    }

    return {
        connector: active.connector as MutableRefObject<Connector | null>,
        connectorType,
        assignMaterial,
        assignAlternateMaterial,
        assignSurfaceTreatment,
        sendAssignment: active.sendAssignment,
        sendMTL: connectorType === 'creo' ? () => creo.sendMTL(pendingMTLData.current, assignment.alternateMaterials) : undefined,
        toast,
    };
}
